import CardTable, {
  type CardTableRowRendererProps,
} from '@/components/Tables/CardTable';
import { CircularProgress, Tag, Tooltip, useLocaleFormat } from '@eye-able/ui';
import type { ColumnDefMeta, TableRef } from '@eye-able/ui';
import { CircleSlash2, FileText, Target } from 'lucide-react';
import { useMemo } from 'react';
import { forwardRef } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useNavigate } from 'react-router-dom';
import { currentAuditEntryIdAtom } from '@/global-store';
import { useAtom } from 'jotai/react';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';

export type ReportRow = {
  id?: number;
  domain?: string;
  summaryDetailScore?: number | string;
  conformanceTarget?: string;
  lastTotalPagesChecked?: number;
  lastAvgErrorCount?: number;
};

export interface HomeReportTableProps {
  data: ReportRow[];
}

const HomeReportTable = forwardRef<TableRef<ReportRow>, HomeReportTableProps>(
  function HomeReportTable({ data }, ref) {
    const columns = useColumns();

    const onRowClick = useOnRowClick();

    return (
      <CardTable
        ref={ref}
        data={data}
        columns={columns}
        getRowId={(row: ReportRow, index: number) =>
          row?.id ?? row?.domain ?? index
        }
        rowTitle={RowTitle}
        rowMain={RowMain}
        searchColumnId='domain'
        defaultSortBy='domain'
        defaultSortDirection='desc'
        onRowClick={onRowClick}
      />
    );
  },
);

const StaticInfo = ({ row }: { row: ReportRow }) => {
  const { t } = useInlineTranslation();

  const { formatNumber } = useLocaleFormat();

  function getConformanceLevelString(target?: string | null) {
    if (!target) return '';

    const levels = [];

    if (target.includes('AAA')) levels.push('AAA');
    else if (target.includes('AA')) levels.push('AA');
    else if (target.includes('A')) levels.push('A');

    if (target.includes('Best Practice')) levels.push('Best Practice');

    return levels.join(', ');
  }

  return (
    <div className='eye:flex eye:flex-wrap eye:items-center eye:gap-2xs eye:body-s'>
      <Tooltip content={t('target')}>
        <div className='eye:flex eye:items-center eye:gap-5xs'>
          <Target
            aria-label={t('target')}
            className='eye:stroke-[1.5] eye:size-16'
          />
          <span>{getConformanceLevelString(row.conformanceTarget)}</span>
        </div>
      </Tooltip>
      <Tooltip content={t('numberOfPages')}>
        <div className='eye:flex eye:items-center eye:gap-5xs'>
          <FileText
            aria-label={t('numberOfPages')}
            className='eye:stroke-[1.5] eye:size-16'
          />
          <span>{row.lastTotalPagesChecked ?? 0}</span>
        </div>
      </Tooltip>
      <Tooltip content={t('averageErrorCount')}>
        <div className='eye:flex eye:items-center eye:gap-5xs'>
          <CircleSlash2
            aria-label={t('averageErrorCount')}
            className='eye:stroke-[1.5] eye:size-16'
          />
          <span>{formatNumber(row.lastAvgErrorCount ?? 0)}</span>
        </div>
      </Tooltip>
    </div>
  );
};

const RowTitle = ({ row, view }: CardTableRowRendererProps<ReportRow>) => {
  if (view === 'grid') {
    return (
      <div className='eye:flex eye:flex-col eye:gap-5xs'>
        <div>{row.domain}</div>
        <StaticInfo row={row} />
      </div>
    );
  }
  return row.domain;
};

const RowMain = ({ row, view }: CardTableRowRendererProps<ReportRow>) => {
  const { t } = useInlineTranslation();

  const score = Math.min(
    Math.max(Number.parseFloat(String(row.summaryDetailScore)) || 0, 0),
    100,
  );

  if (view === 'grid') {
    return (
      <div className='eye:min-h-[140px] eye:p-s'>
        <Tooltip content={t('overallRating')}>
          <span>
            <CircularProgress
              value={score}
              ariaLabel={t('overallRating')}
              size='default'
            />
          </span>
        </Tooltip>
      </div>
    );
  }

  return (
    <div className='eye:flex eye:w-full eye:justify-between'>
      <StaticInfo row={row} />

      <Tooltip content={t('overallRating')}>
        <span>
          <CircularProgress
            value={score}
            ariaLabel={t('overallRating')}
            size='small'
          />
        </span>
      </Tooltip>
    </div>
  );
};

function useOnRowClick() {
  const navigate = useNavigate();
  const [, setCurrentAuditEntryId] = useAtom(currentAuditEntryIdAtom);

  return function onRowClick(row: ReportRow) {
    if (typeof row.id === 'number') {
      setCurrentAuditEntryId(row.id);
    }
    navigate('/report/overview');
  };
}

function useColumns() {
  const { t } = useInlineTranslation();

  return useMemo<ColumnDef<ReportRow, unknown>[]>(() => {
    const parseConformanceTokens = (value?: string | null): string[] => {
      if (!value) return [];

      return value
        .split(/[+,]/)
        .map((token) => token.trim())
        .filter(Boolean);
    };
    return [
      {
        id: 'domain',
        accessorKey: 'domain',
        header: t('domain'),
        sortDescFirst: true,
        enableSorting: true,
        enableColumnFilter: true,
        meta: {
          filterVariant: 'text',
        } as ColumnDefMeta,
      },
      {
        id: 'summaryDetailScore',
        accessorFn: (row) => {
          const value = row.summaryDetailScore;
          if (value === null || value === undefined || value === '') return 0;
          const parsed = Number.parseFloat(String(value));
          return Number.isNaN(parsed) ? 0 : parsed;
        },
        header: t('overallRating'),
        sortDescFirst: true,
        enableColumnFilter: false,
      },
      {
        id: 'conformanceTarget',
        accessorKey: 'conformanceTarget',
        header: t('target'),
        sortDescFirst: true,
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: (row, columnId, filterValues) => {
          if (!Array.isArray(filterValues) || filterValues.length === 0) {
            return true;
          }

          const cellValue = row.getValue<string | null | undefined>(columnId);
          const tokens = parseConformanceTokens(cellValue);

          return filterValues.every((selected) =>
            tokens.includes(String(selected)),
          );
        },
        meta: {
          filterVariant: 'multiselect',
          filterOptions: ['A', 'AA', 'AAA', 'Best Practice'],
          filterRenderer: (item: { value: string }) => <Tag>{item.value}</Tag>,
        } as ColumnDefMeta,
      },
      {
        id: 'lastTotalPagesChecked',
        accessorKey: 'lastTotalPagesChecked',
        header: t('numberOfPages'),
        sortDescFirst: true,
        enableColumnFilter: true,
        meta: {
          filterVariant: 'range',
        } as ColumnDefMeta,
      },
      {
        id: 'lastAvgErrorCount',
        accessorKey: 'lastAvgErrorCount',
        header: t('averageErrorCount'),
        sortDescFirst: true,
        enableColumnFilter: true,
        meta: {
          filterVariant: 'range',
        } as ColumnDefMeta,
      },
    ];
  }, [t]);
}

export default HomeReportTable;
